import { useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { useNavigate } from "@opendash/router";
import { Button, Card, Popconfirm, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { CSSProperties, useEffect, useState } from "react";
import { Route, Route_Type, Stop_Times, Trip } from "../js/parsegenerated";
import { RouteUpdateModal } from "./RouteUpdateModal";

export const RouteCard = observer(
  (props: { route: Route; reload: () => void }) => {
    const [selectedRoute, setSelectedRoute] = useState<Route>();
    const [routeType, setRouteType] = useState<Route_Type>();
    const [updateModalVisible, setUpdateModalVisible] =
      useState<boolean>(false);
    const t = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
      if (props.route.get("route_type") !== undefined) {
        new Parse.Query(Route_Type)
          .equalTo("value", props.route.get("route_type"))
          .first()
          .then((routeType) => {
            setRouteType(routeType);
          });
      }
    }, [props.route.get("route_type")]);

    const getStyle = (route: Route): CSSProperties => {
      const borderColor = route.get("route_color");

      if (borderColor && borderColor.length > 0) {
        return {
          borderLeftColor: borderColor,
          borderLeftWidth: 5,
          borderRadius: 5,
        };
      } else {
        return {
          borderLeftColor: "var(--opendash-color-lightgray)",
          borderLeftWidth: 1,
          borderRadius: 5,
        };
      }
    };

    const deleteRoute = async () => {
      // Find the trips that should be deleted.
      const trips = await new Parse.Query(Trip)
        .equalTo("route_id", {
          __type: "Pointer",
          className: Route.className,
          objectId: props.route.id,
        })
        .find();

      // Find the stop_times that should be deleted.
      const stop_times_promises = trips.map(async (trip) => {
        return await new Parse.Query(Stop_Times)
          .equalTo("trip_id", {
            __type: "Pointer",
            className: Trip.className,
            objectId: trip.id,
          })
          .find();
      });

      // Delete all collected objects
      for await (const stop_times of stop_times_promises) {
        stop_times.map(async (stop_time) => await stop_time.destroy());
      }
      for await (const trip of trips) {
        await trip.destroy();
      }
      await props.route.destroy();

      props.reload();
    };

    return (
      <>
        <Card
          title={
            props.route
              ? props.route.get("route_short_name") +
                " " +
                props.route.get("route_long_name")
              : null
          }
          style={getStyle(props.route)}
          styles={{
            header: {
              backgroundColor: props.route.get("route_color"),
              color: props.route.get("route_text_color"),
            },
          }}
          actions={[
            <Tooltip
              title={t("gtfs:agency.detail.card.actions.trips.retrieve")}
            >
              <Button
                type="text"
                icon={<Icon icon="fa:route" />}
                onClick={() => {
                  navigate("/gtfs/route/" + props.route.id + "/trips");
                }}
              ></Button>
            </Tooltip>,

            <Popconfirm
              title={t("gtfs:agency.detail.card.actions.delete.title")}
              onConfirm={deleteRoute}
              onCancel={() => {}}
              okText={t("gtfs:global.button.confirm_label")}
              cancelText={t("gtfs:global.button.cancel_label")}
            >
              <Button
                icon={<Icon color="red" icon="fa:trash" />}
                shape="circle"
                type="text"
              ></Button>
            </Popconfirm>,
            <Button
              onClick={() => {
                setSelectedRoute(props.route);
                setUpdateModalVisible(true);
              }}
              icon={<Icon icon="fa:pen" />}
              shape="circle"
              type="text"
            ></Button>,
          ]}
        >
          <p>
            {t("gtfs:agency.detail.card.info.route_type", {
              type: routeType?.get("transportation_device"),
            })}
          </p>
          {props.route.get("route_url") && (
            <p>
              {t("gtfs:agency.detail.card.info.route_url")}
              <a
                href={props.route.get("route_url")}
                target="_blank"
                rel="noreferrer"
              >
                {props.route.get("route_url")}
              </a>
            </p>
          )}
          <p>{props.route.get("route_desc")}</p>
        </Card>

        <RouteUpdateModal
          open={updateModalVisible}
          route={selectedRoute}
          onOk={() => {
            setUpdateModalVisible(false);
            setSelectedRoute(undefined);
            props.reload();
          }}
          onCancel={() => {
            setUpdateModalVisible(false);
            setSelectedRoute(undefined);
          }}
        />
      </>
    );
  }
);
